/*
 * HC product page: the per-variant pieces Crave does not update.
 *
 * assets/product-info.js handles a variant change by fetching the section HTML
 * and swapping only the fragments it knows about: price, Sku, Inventory,
 * Volume, Price-Per-Item, the media gallery and the submit button's disabled
 * state. Everything the HC product page renders per variant is invisible to
 * that code, so this file subscribes to the same PUB_SUB_EVENTS.variantChange
 * event product-info.js publishes and swaps the rest by id from the very same
 * fetched document. It never refetches and never repeats the variant lookup:
 * both the document and the variant arrive on the event.
 *
 * Swapped by id (each suffixed with the section id):
 *   HcAttrPills-<section id>   format, mesh and availability chips
 *   HcIncluded-<section id>    the "What's included" copy for this Format
 *   HcCtaPrice-<section id>    the price span inside the add-to-cart button
 *   HcStickyPrice-<section id> the sticky bar price
 *   HcStickyMeta-<section id>  the sticky bar "Format · mesh" line
 *   HcProductMedia-<section id> the square media frame
 * plus data-hc-variant-id on the sticky bar itself.
 *
 * The media frame is swapped from the same document but is deliberately not one
 * of SWAP_IDS, because SWAP_IDS is also the list that gets hidden when an option
 * combination resolves to no variant, and blanking the photo is the wrong answer
 * to "that size is unavailable". Swapping it replaces the whole
 * <hc-media-frame> element inside the wrapper, so assets/hc-product-media.js
 * binds the new one through connectedCallback and the hero/detail toggle starts
 * over on the hero. Crave's own updateMedia in product-info.js finds no
 * <media-gallery ul> and no product modal on this page and quietly does nothing,
 * which is what its optional chaining is there for.
 *
 * An option combination with no variant never publishes variantChange at all:
 * product-info.js calls setUnavailable() and returns. setUnavailable() carries
 * one HC addition for that reason -- it dispatches `hc:variant-unavailable` on
 * the product-info element -- and every registered instance listens for it and
 * hides the same five fragments. That signal is independent of the optional
 * native price block, which a merchant may remove when the price is shown on
 * the CTA. The next successful variantChange restores them: swapById mirrors
 * each fragment's own `hidden` state from the fetched document, so a fragment
 * hidden here is un-hidden again as soon as the source is visible.
 *
 * The CTA price is its own span beside Crave's add-to-cart span, never inside
 * it: product-form.js resets `submitButton.querySelector('span').textContent`
 * to window.variantStrings.addToCart after every add, and that first span is
 * Crave's. window.variantStrings is not touched here.
 *
 * Sticky bar. It carries no form; its button has form="<product form id>", so
 * it submits the canonical product form. Its disabled attribute is mirrored
 * from the main submit button through a MutationObserver, which means it goes
 * inert the instant Crave's handleOptionValueChange calls resetProductFormState
 * and stays inert if the fetch resolves unavailable or fails. Selecting a
 * format and clicking the sticky button before the fetch resolves therefore
 * does nothing. Visibility is an IntersectionObserver on the main buy button:
 * the bar is hidden whenever that button is on screen.
 *
 * Registration is per <product-info> element, not per page, so a quick-add
 * dialog with its own product-info works alongside the page's, and
 * shopify:section:load re-registers after a Theme Editor section reload.
 *
 * Routing. variantChange carries a `target` -- the product-info instance that
 * started the fetch, added to product-info.js's payload for this -- so the
 * response is applied to that element and to nothing else. Section id alone
 * cannot do it: a quick-add dialog keeps the section id of the product-info it
 * was fetched from, so two products sharing one product template publish the
 * same sectionId. sectionId keeps its one job here, naming the ids inside the
 * fetched document. Cancellation needs no bookkeeping: an aborted or failed
 * fetch never publishes variantChange, and nothing is queued waiting for it, so
 * there is no stale entry a later response could consume. A payload without a
 * target is ignored rather than guessed at.
 *
 * The subscriptions are installed once per page. Quick-add injects the section
 * HTML with HTMLUpdateUtility.setInnerHTML, which re-executes this file's
 * script tag, so a window-level flag keeps the handlers from stacking up;
 * per-instance registration still runs on every execution and is idempotent
 * through the data-hc-variant-bound guard.
 */
(function () {
  'use strict';

  var SWAP_IDS = ['HcAttrPills', 'HcIncluded', 'HcCtaPrice', 'HcStickyPrice', 'HcStickyMeta'];
  var MEDIA_SWAP_ID = 'HcProductMedia';
  var BOUND = 'hcVariantBound';

  /* Mirrors product-info.js updateSourceFromDestination: the fetched document
     is keyed by the event's section id, the live DOM by this element's own
     data-section. In a quick-add dialog those two differ. */
  function swapById(productInfo, html, baseId, sourceSectionId) {
    var source = html.getElementById(baseId + '-' + sourceSectionId);
    var destination = productInfo.querySelector('#' + baseId + '-' + productInfo.dataset.section);
    if (!source || !destination) return;

    destination.innerHTML = source.innerHTML;
    if (source.hasAttribute('hidden')) {
      destination.setAttribute('hidden', '');
    } else {
      destination.removeAttribute('hidden');
    }
  }

  function mainSubmitButton(productInfo) {
    return productInfo.querySelector('.product-form__submit');
  }

  function stickyBar(productInfo) {
    return productInfo.querySelector('[data-hc-sticky-atc]');
  }

  /* Mirror disabled and aria-disabled from the real submit button. aria-disabled
     is what product-form.js sets while an add is in flight; disabled is what a
     sold-out or unresolved variant sets. Either one must make the sticky button
     inert. */
  function bindDisabledMirror(productInfo) {
    var main = mainSubmitButton(productInfo);
    var bar = stickyBar(productInfo);
    if (!main || !bar) return;

    var sticky = bar.querySelector('[data-hc-sticky-submit]');
    if (!sticky) return;

    var sync = function () {
      ['disabled', 'aria-disabled'].forEach(function (name) {
        if (main.hasAttribute(name)) {
          sticky.setAttribute(name, main.getAttribute(name) || name);
        } else {
          sticky.removeAttribute(name);
        }
      });
    };

    new MutationObserver(sync).observe(main, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
    });
    sync();
  }

  /* Crave's unavailable path never reaches the swap. In product-info.js,
     handleUpdateProductInfo calls setUnavailable() and returns BEFORE
     publishing variantChange when the chosen option combination resolves to no
     variant, so every HC fragment would keep showing the price, pills and
     included copy of the last real variant beside Crave's "Unavailable".

     setUnavailable() dispatches `hc:variant-unavailable` on the product-info
     element for exactly that case -- one of the two small HC additions to that
     file -- which is a direct signal rather than an inference from a
     presentation node the merchant may have removed. Restoring is left to the
     next successful variantChange, which mirrors each fragment's own hidden
     state from the fetched document. */
  function bindUnavailableMirror(productInfo) {
    productInfo.addEventListener('hc:variant-unavailable', function () {
      SWAP_IDS.forEach(function (baseId) {
        var fragment = productInfo.querySelector('#' + baseId + '-' + productInfo.dataset.section);
        if (fragment) fragment.setAttribute('hidden', '');
      });
    });
  }

  /* The bar rides up once the main buy button has left the viewport. */
  function bindVisibility(productInfo) {
    var main = mainSubmitButton(productInfo);
    var bar = stickyBar(productInfo);
    if (!main || !bar) return;

    var sticky = bar.querySelector('[data-hc-sticky-submit]');

    var setVisible = function (on) {
      bar.classList.toggle('is-visible', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (sticky) sticky.setAttribute('tabindex', on ? '0' : '-1');
    };

    if (typeof IntersectionObserver !== 'function') {
      setVisible(false);
      return;
    }

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          setVisible(!entry.isIntersecting);
        });
      },
      { threshold: 0 }
    ).observe(main);

    setVisible(false);
  }

  function register(productInfo) {
    if (!productInfo || productInfo.dataset[BOUND] === 'true') return;
    productInfo.dataset[BOUND] = 'true';

    bindDisabledMirror(productInfo);
    bindUnavailableMirror(productInfo);
    bindVisibility(productInfo);
  }

  function registerAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('product-info'), register);
    if (root && root.tagName === 'PRODUCT-INFO') register(root);
  }

  function productInfoFrom(node) {
    return node && node.closest ? node.closest('product-info') : null;
  }

  /* data.target is the product-info that started the fetch. Without it there is
     no safe answer -- section id is shared across quick-add instances of the
     same template -- so nothing is updated. */
  function onVariantChange(event) {
    var data = (event && event.data) || {};
    var html = data.html;
    var sourceSectionId = data.sectionId;
    if (!html || !sourceSectionId) return;

    var productInfo = productInfoFrom(data.target);
    if (!productInfo) return;

    SWAP_IDS.forEach(function (baseId) {
      swapById(productInfo, html, baseId, sourceSectionId);
    });
    swapById(productInfo, html, MEDIA_SWAP_ID, sourceSectionId);

    var bar = stickyBar(productInfo);
    if (bar) bar.dataset.hcVariantId = data.variant && data.variant.id ? String(data.variant.id) : '';
  }

  function start() {
    registerAll(document);

    /* Quick-add re-executes this script with every dialog it opens; the
       subscriptions and the section:load listener belong to the page, so they
       are installed exactly once. Registration above is not guarded here: it
       has to run again to pick up the new instance. */
    if (window.__hcProductVariantSubscribed) return;
    window.__hcProductVariantSubscribed = true;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    }

    document.addEventListener('shopify:section:load', function (event) {
      registerAll(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
